import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { resolveQrToken } from '../api/qr';

const Container = styled.div`
  min-height: calc(100vh - var(--header-height));
  padding: 2rem 1rem;
  max-width: 600px;
  margin: 0 auto;
  margin-top: var(--header-height);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StateBox = styled.div`
  background: var(--white);
  border-radius: 8px;
  padding: 3rem 2rem;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  color: var(--gray-dark);
  font-size: 1rem;
  width: 100%;
`;

const InvalidState = styled(StateBox)`
  color: #991B1B;
  background-color: #FEF2F2;
`;

const InvalidTitle = styled.h1`
  font-size: 1.25rem;
  margin-bottom: 0.5rem;
`;

const HomeButton = styled.button`
  padding: 0.75rem 2rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  background-color: var(--primary-color);
  color: #FFFFFF;
  margin-top: 1.5rem;

  &:hover {
    background-color: #b8891a;
  }
`;

const QRRedirect: React.FC = () => {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'invalid' | 'error'>('loading');

  useEffect(() => {
    let cancelled = false;

    const resolve = async () => {
      if (!token) {
        if (!cancelled) setStatus('invalid');
        return;
      }

      try {
        const data = await resolveQrToken(token);
        if (!cancelled) {
          navigate(data.path, { replace: true });
        }
      } catch (err: any) {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setStatus('invalid');
        } else {
          setStatus('error');
        }
      }
    };

    resolve();

    return () => {
      cancelled = true;
    };
  }, [token, navigate]);

  if (status === 'invalid') {
    return (
      <Container>
        <InvalidState>
          <InvalidTitle>{t('qrRedirect.invalidTitle')}</InvalidTitle>
          <div>{t('qrRedirect.invalidMessage')}</div>
          <HomeButton onClick={() => navigate('/')}>{t('qrRedirect.returnHome')}</HomeButton>
        </InvalidState>
      </Container>
    );
  }

  if (status === 'error') {
    return (
      <Container>
        <InvalidState>
          <InvalidTitle>{t('qrRedirect.errorTitle')}</InvalidTitle>
          <div>{t('qrRedirect.errorMessage')}</div>
          <HomeButton onClick={() => navigate('/')}>{t('qrRedirect.returnHome')}</HomeButton>
        </InvalidState>
      </Container>
    );
  }

  return (
    <Container>
      <StateBox>{t('qrRedirect.loading')}</StateBox>
    </Container>
  );
};

export default QRRedirect;
